import { z } from "zod";
import {
  SocialAnalysisStatusSchema,
  SocialCaptureStatusSchema,
  SocialEvidenceKindSchema,
  SocialExtractionStatusSchema,
  SocialFindingSeveritySchema,
  SocialJudgmentSchema,
  SocialMediaKindSchema,
  SocialPlatformSchema,
  SocialPostKindSchema,
  SocialProfileStatusSchema,
  SocialStandardSchema,
} from "../../enums/social";
import { SocialEngagementSchema } from "./capture";

export const SocialProfileCreateRequestSchema = z
  .object({
    clientId: z.uuid(),
    platform: SocialPlatformSchema,
    profile: z.string().trim().min(1),
  })
  .strict();
export type SocialProfileCreateRequest = z.infer<
  typeof SocialProfileCreateRequestSchema
>;

export const SocialProfileResponseSchema = z.object({
  id: z.uuid(),
  clientId: z.uuid(),
  platform: SocialPlatformSchema,
  profileUrl: z.url(),
  handle: z.string(),
  providerAccountId: z.string().nullable(),
  displayName: z.string().nullable(),
  avatarUrl: z.url().nullable(),
  status: SocialProfileStatusSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type SocialProfileResponse = z.infer<typeof SocialProfileResponseSchema>;

export const SocialExtractionResponseSchema = z.object({
  id: z.uuid(),
  profileId: z.uuid(),
  status: SocialExtractionStatusSchema,
  attemptId: z.uuid().nullable(),
  providerRunId: z.string().nullable(),
  providerStatus: z.string().nullable(),
  datasetOffset: z.number().int().min(0),
  acceptedPosts: z.number().int().min(0),
  rejectedItems: z.number().int().min(0),
  error: z.string().nullable(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
});
export type SocialExtractionResponse = z.infer<
  typeof SocialExtractionResponseSchema
>;

export const SocialCaptureAssetResponseSchema = z.object({
  id: z.uuid(),
  kind: SocialMediaKindSchema,
  contentType: z.string(),
  sha256: z.string(),
  byteLength: z.number().int(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  durationMs: z.number().int().nullable(),
  altText: z.string().nullable(),
  url: z.url(),
});

export const SocialAnalysisEvidenceResponseSchema = z.object({
  key: z.string(),
  kind: SocialEvidenceKindSchema,
  assetId: z.uuid().nullable(),
  excerpt: z.string().nullable(),
  description: z.string(),
});

export const SocialAnalysisJudgmentResponseSchema = z.object({
  standard: SocialStandardSchema,
  judgment: SocialJudgmentSchema,
  rationale: z.string(),
  evidenceKeys: z.array(z.string()),
});

export const SocialAnalysisFindingResponseSchema = z.object({
  severity: SocialFindingSeveritySchema,
  title: z.string(),
  detail: z.string(),
  recommendedAction: z.string(),
  evidenceKeys: z.array(z.string()),
});

export const SocialAnalysisResponseSchema = z.object({
  attemptId: z.uuid(),
  status: SocialAnalysisStatusSchema,
  summary: z.string().nullable(),
  error: z.string().nullable(),
  evidence: z.array(SocialAnalysisEvidenceResponseSchema),
  judgments: z.array(SocialAnalysisJudgmentResponseSchema),
  findings: z.array(SocialAnalysisFindingResponseSchema),
  createdAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
});
export type SocialAnalysisResponse = z.infer<
  typeof SocialAnalysisResponseSchema
>;

export const SocialCaptureResponseSchema = z.object({
  id: z.uuid(),
  profileId: z.uuid(),
  platform: SocialPlatformSchema,
  externalPostId: z.string(),
  authorHandle: z.string(),
  authorDisplayName: z.string().nullable(),
  canonicalUrl: z.url(),
  kind: SocialPostKindSchema,
  text: z.string(),
  publishedAt: z.iso.datetime(),
  engagement: SocialEngagementSchema,
  bundleSha256: z.string(),
  status: SocialCaptureStatusSchema,
  assets: z.array(SocialCaptureAssetResponseSchema),
  analysis: SocialAnalysisResponseSchema.nullable(),
  createdAt: z.iso.datetime(),
});
export type SocialCaptureResponse = z.infer<typeof SocialCaptureResponseSchema>;

export const SocialWorkspaceResponseSchema = z.object({
  profiles: z.array(SocialProfileResponseSchema),
  extractions: z.array(SocialExtractionResponseSchema),
  captures: z.array(SocialCaptureResponseSchema),
});
export type SocialWorkspaceResponse = z.infer<
  typeof SocialWorkspaceResponseSchema
>;

export const SocialExtractionStartResponseSchema = z.object({
  runId: z.uuid(),
  attemptId: z.uuid(),
});
export const SocialExtractionStopResponseSchema = z.object({
  runId: z.uuid(),
  status: SocialExtractionStatusSchema,
});
export const SocialExtractionResumeResponseSchema = z.object({
  runId: z.uuid(),
  attemptId: z.uuid(),
  created: z.boolean(),
});
